import React, {useState} from "react"
import {useModal} from "react-modal-hook"
import {useNavigate} from "react-router-dom"
import {OkDocument} from "../../resources/common"
import {IndividualProductApplication} from "../../resources/productApplication"
import ReevaluateIndividualProductApplicationConfirmationModal from "../../components/ReevaluateProductApplication/ReevaluateProductApplicationConfirmationModal/ReevaluateIndividualProductApplicationConfirmationModal"
import ProductApplicationDetails from "./ProductApplicationDetails"
import {identificationFieldBeforeUpdateRequest} from "../ReevaluateApplication/ApplicationDetails"
import {MetaItemProps} from "../../components/TitleBar/TitleBar"
import {ProductApplicationResubmitForms} from "./ReevaluateProductApplicationPage"
import ReevaluateIndividualProductApplicationForm, {
	ReevaluatedIndividualProductApplicationData,
} from "../../components/ReevaluateProductApplication/Forms/ReevaluateIndividualProductApplicationForm/ReevaluateIndividualProductApplicationForm"
import Admonition from "../../components/Admonitions/Admonition"

interface ReevaluateIndividualProductApplicationPageProps {
	application: OkDocument<IndividualProductApplication>
}

function toIndividualFormData(
	application: IndividualProductApplication
): ReevaluatedIndividualProductApplicationData | undefined {
	if (!application.attributes.address || !application.attributes.dateOfBirth) {
		return undefined
	}

	return {
		fullName: application.attributes.fullName,
		address: application.attributes.address,
		ssn: application.attributes.ssn ?? undefined,
		nationality: application.attributes.nationality ?? undefined,
		passport: application.attributes.passport ?? undefined,
		matriculaConsular: application.attributes.matriculaConsular ?? undefined,
		dateOfBirth: application.attributes.dateOfBirth,
		email: application.attributes.email ?? undefined,
		phone: application.attributes.phone,
		ein: application.attributes.ein ?? undefined,
		dba: application.attributes.dba,
		industry: application.attributes.industry ?? undefined,
		occupation: application.attributes.occupation ?? undefined,
		annualIncome: application.attributes.annualIncome ?? undefined,
		sourceOfIncome: application.attributes.sourceOfIncome ?? undefined,
		annualRevenue: application.attributes.annualRevenue ?? undefined,
		numberOfEmployees: application.attributes.numberOfEmployees ?? undefined,
		businessVertical: application.attributes.businessVertical ?? undefined,
		website: application.attributes.website,
	}
}

export default function ReevaluateIndividualProductApplicationContainer({
	application,
}: ReevaluateIndividualProductApplicationPageProps) {
	const navigate = useNavigate()
	const individualFormData = toIndividualFormData(application.data)

	const [individualReevaluatedData, setIndividualReevaluatedData] = useState<
		ReevaluatedIndividualProductApplicationData | undefined
	>(individualFormData)
	const [conversionError, setConversionError] = useState(false)

	const [showConfirmationModal, closeConfirmationModal] = useModal(() => {
		if (!individualReevaluatedData) {
			setConversionError(true)
			return <></>
		}
		return (
			<ReevaluateIndividualProductApplicationConfirmationModal
				onSuccess={() => navigate(`/product-applications/${application.data.id}`)}
				close={closeConfirmationModal}
				application={application.data}
				reevaluatedData={individualReevaluatedData}
			/>
		)
	}, [individualReevaluatedData])

	const {first, last} = application.data.attributes.fullName
	const title = `${first} ${last}`
	const meta: MetaItemProps[] = [{text: "Individual", icon: "user-geometric-action---users"}]

	if (conversionError) {
		return (
			<Admonition
				type="is-danger"
				title="Failed to process resubmission data, please reach administrator."
				message={""}
			/>
		)
	}

	if (!individualFormData) {
		return (
			<Admonition
				type="is-danger"
				title="Could not be able to show resubmit form, please reach administator."
				message={""}
			/>
		)
	}

	return (
		<ProductApplicationDetails
			title={title}
			meta={meta}
			productApplicationId={application.data.id}
			status={application.data.attributes.status}
			formId={ProductApplicationResubmitForms.individualForm}
		>
			<ReevaluateIndividualProductApplicationForm
				formId={ProductApplicationResubmitForms.individualForm}
				application={application}
				disableSensitiveFields
				submitCallback={(values: ReevaluatedIndividualProductApplicationData) => {
					const currentValues = {
						ssn: application.data.attributes.ssn,
						passport: application.data.attributes.passport,
						matriculaConsular: application.data.attributes.matriculaConsular,
					}
					const identificationFields = identificationFieldBeforeUpdateRequest({
						currentValues: currentValues,
						updatedValues: values,
					})

					setIndividualReevaluatedData({
						...values,
						...identificationFields,
					})
					showConfirmationModal()
				}}
			/>
		</ProductApplicationDetails>
	)
}
